import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Bell, FileText } from 'lucide-react';
import { useState } from 'react';
import { RealTimeMessage } from '../hooks/useRealTimeMessages';

interface NotificationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode?: boolean;
  notifications: RealTimeMessage[];
  notices: RealTimeMessage[];
  markAsRead: (id: number) => void;
}

type TabType = 'notifications' | 'notices';

export default function NotificationSidebar({ isOpen, onClose, notifications, notices, markAsRead }: NotificationSidebarProps) {
  const [activeTab, setActiveTab] = useState<TabType>('notifications');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 cursor-pointer"
          />

          {/* Drawer Panel */}
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: 'easeOut', duration: 0.3 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[400px] bg-white dark:bg-[#161616] border-l border-gray-100 dark:border-white/5 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
              <h3 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">বার্তা কেন্দ্র</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-50 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-[#A0A5AD] hover:bg-gray-100 dark:hover:bg-white/10 active:scale-95 transition-all cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Apple Style Segment Selector */}
            <div className="px-6 py-4">
              <div className="relative bg-gray-100 dark:bg-white/5 rounded-2xl p-1 flex">
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`relative flex-1 py-2 font-bangla text-sm font-semibold rounded-xl transition-all cursor-pointer z-10 ${
                    activeTab === 'notifications'
                      ? 'text-[#1F1F1F] dark:text-[#121212]'
                      : 'text-gray-500 dark:text-[#A0A5AD]'
                  }`}
                >
                  {activeTab === 'notifications' && (
                    <motion.div
                      layoutId="activeNotificationTab"
                      className="absolute inset-0 bg-white dark:bg-[#8AB4F8] rounded-xl shadow-sm -z-10"
                      transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                    />
                  )}
                  নোটিফিকেশন
                </button>
                <button
                  onClick={() => setActiveTab('notices')}
                  className={`relative flex-1 py-2 font-bangla text-sm font-semibold rounded-xl transition-all cursor-pointer z-10 ${
                    activeTab === 'notices'
                      ? 'text-[#1F1F1F] dark:text-[#121212]'
                      : 'text-gray-500 dark:text-[#A0A5AD]'
                  }`}
                >
                  {activeTab === 'notices' && (
                    <motion.div
                      layoutId="activeNotificationTab"
                      className="absolute inset-0 bg-white dark:bg-[#8AB4F8] rounded-xl shadow-sm -z-10"
                      transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                    />
                  )}
                  নোটিশ
                </button>
              </div>
            </div>

            {/* Content List */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
              <AnimatePresence mode="wait">
                {activeTab === 'notifications' ? (
                  <motion.div
                    key="notifications"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    {notifications.length === 0 ? (
                      <div className="py-12 text-center text-gray-400 dark:text-[#A0A5AD]/60 font-bangla text-xs">
                        কোনো নতুন নোটিফিকেশন নেই
                      </div>
                    ) : (
                      notifications.map((item) => {
                        const Icon = item.icon || Bell;
                        return (
                          <div
                            key={item.id}
                            onClick={() => markAsRead(item.id)}
                            className={`p-4 rounded-2xl border transition-all flex gap-3 relative overflow-hidden cursor-pointer ${
                              item.unread
                                ? 'bg-blue-50/20 dark:bg-[#8AB4F8]/5 border-blue-100/30 dark:border-[#8AB4F8]/10'
                                : 'bg-white dark:bg-[#1E1E1E]/50 border-gray-50 dark:border-white/5'
                            }`}
                          >
                            {item.unread && (
                              <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-blue-500" />
                            )}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.iconColor || 'text-blue-500 bg-blue-500/10'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <h4 className="font-bangla text-sm font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{item.title}</h4>
                              <p className="font-bangla text-xs text-gray-500 dark:text-[#A0A5AD] mt-1 leading-relaxed">{item.description}</p>
                              <span className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD]/60 mt-2 block">{item.time}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="notices"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    {notices.length === 0 ? (
                      <div className="py-12 text-center text-gray-400 dark:text-[#A0A5AD]/60 font-bangla text-xs">
                        কোনো নতুন নোটিশ নেই
                      </div>
                    ) : (
                      notices.map((item) => {
                        const Icon = item.icon || FileText;
                        return (
                          <div
                            key={item.id}
                            onClick={() => markAsRead(item.id)}
                            className={`p-4 rounded-2xl border transition-all flex gap-3 relative overflow-hidden cursor-pointer ${
                              item.unread
                                ? 'bg-emerald-50/20 dark:bg-emerald-500/5 border-emerald-100/30 dark:border-emerald-500/10'
                                : 'bg-white dark:bg-[#1E1E1E]/50 border-gray-50 dark:border-white/5'
                            }`}
                          >
                            {item.unread && (
                              <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-emerald-500" />
                            )}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.iconColor || 'text-emerald-500 bg-emerald-500/10'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <h4 className="font-bangla text-sm font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{item.title}</h4>
                              <p className="font-bangla text-xs text-gray-500 dark:text-[#A0A5AD] mt-1 leading-relaxed">{item.description}</p>
                              <div className="flex items-center gap-1 mt-2 text-gray-400 dark:text-[#A0A5AD]/60">
                                <Calendar className="w-3.5 h-3.5" />
                                <span className="font-bangla text-[10px]">{item.date}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
